import { revalidateLogic, useForm } from '@tanstack/react-form';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AreaConfig,
  AreaConfigResponse,
  GlobalConfig,
  StructureGridCollectionDetail,
  TypeCurveScenarioFormData,
} from './types';
import type { Geometry } from 'geojson';
import {
  scenarioFormDefaultValues,
  defaultFallbackTypeWellConfig,
  defaultInventoryConfig,
} from './default-values';
import { useSetAtom } from 'jotai';
import {
  saveAreaConfigAtom,
  updateAreaConfigAtom,
  updateScenarioAtom,
  saveTypeCurveScenarioAtom,
  saveFormationMappingsAtom,
  saveInventoryConfigAtom,
  updateInventoryConfigAtom,
  saveScalingConfigsAtom,
  saveTypeWellConfigsAtom,
  saveValidationConfigsAtom,
  typeCurveScenarioDataAtom,
} from './atoms';
import { isEqual, omit } from 'lodash-es';
// eslint-disable-next-line camelcase
import { unstable_batchedUpdates } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { getChangedObject } from './utils';

export const SCENARIO_DETAILS_ID = 'scenario-details';

export type SetFieldBaselineFn = (
  field: keyof TypeCurveScenarioFormData,
  value: TypeCurveScenarioFormData[keyof TypeCurveScenarioFormData]
) => void;

export type UpdateFieldDefaultFn = (
  field: keyof TypeCurveScenarioFormData,
  value: TypeCurveScenarioFormData[keyof TypeCurveScenarioFormData]
) => void;

type ScenarioFormInstance = ReturnType<typeof useCreateScenarioForm>;

interface ScenarioFormContextType {
  form: ScenarioFormInstance | null;
  defaultValues: TypeCurveScenarioFormData | null;
  baselines: TypeCurveScenarioFormData | null;
  setFieldBaseline: SetFieldBaselineFn | null;
  updateFieldDefault: UpdateFieldDefaultFn | null;
  resetForm: (() => void) | null;
  discardFormChanges: (() => void) | null;
  isFormModified: ((values: TypeCurveScenarioFormData) => boolean) | null;
  areaConfigs: AreaConfigResponse[];
  setAreaConfigs: React.Dispatch<React.SetStateAction<AreaConfigResponse[]>>;
  selectedConfigId: string | null;
  selectedAreaConfig: AreaConfigResponse | null;
  isScenarioDetailsSelected: boolean;
  resetConfigId: (id: string) => void;
  setSelectedConfigId: React.Dispatch<React.SetStateAction<string | null>>;
  handleSubmitForm: (meta: ScenarioFormSubmitMeta) => Promise<void>;
  discardCount: number;
  setDiscardCount: React.Dispatch<React.SetStateAction<number>>;
  saveCount: number;
  readOnly: boolean;
  subAreaGeometries: Record<string, Geometry>;
  setSubAreaGeometries: React.Dispatch<
    React.SetStateAction<Record<string, Geometry>>
  >;
  structureGridCollectionData: StructureGridCollectionDetail | null;
  setStructureGridCollectionData: React.Dispatch<
    React.SetStateAction<StructureGridCollectionDetail | null>
  >;
  validConfigState: { isValid: boolean; message: string } | null;
  setValidConfigState: React.Dispatch<
    React.SetStateAction<{ isValid: boolean; message: string } | null>
  >;
  isValidating: boolean;
  setIsValidating: React.Dispatch<React.SetStateAction<boolean>>;
  manualFormationPriority: boolean;
  setManualFormationPriority: React.Dispatch<React.SetStateAction<boolean>>;
  formationRanks: number[];
  setFormationRanks: React.Dispatch<React.SetStateAction<number[]>>;
  formationsTrigger: number;
  setFormationsTrigger: React.Dispatch<React.SetStateAction<number>>;
  /**
   * The id of the area config whose mappings the Formations table holds, or
   * null while they are still loading. The Type Well Profile Configs section
   * waits for this before building its rows from the table.
   */
  formationMappingsReadyFor: string | null;
  setFormationMappingsReadyFor: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  savedInventoryConfigHref: string | null;
  setSavedInventoryConfigHref: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  focusedSubAreaId: string | null;
  setFocusedSubAreaId: React.Dispatch<React.SetStateAction<string | null>>;
}

export const ScenarioFormContext = createContext<ScenarioFormContextType>({
  form: null,
  defaultValues: null,
  baselines: null,
  setFieldBaseline: null,
  updateFieldDefault: null,
  resetForm: null,
  discardFormChanges: null,
  isFormModified: null,
  areaConfigs: [],
  setAreaConfigs: () => {},
  selectedConfigId: SCENARIO_DETAILS_ID,
  selectedAreaConfig: null,
  isScenarioDetailsSelected: true,
  resetConfigId: () => {},
  setSelectedConfigId: () => {},
  handleSubmitForm: () => Promise.resolve(),
  discardCount: 0,
  setDiscardCount: () => {},
  saveCount: 0,
  readOnly: false,
  subAreaGeometries: {},
  setSubAreaGeometries: () => {},
  structureGridCollectionData: null,
  setStructureGridCollectionData: () => {},
  validConfigState: null,
  setValidConfigState: () => {},
  isValidating: false,
  setIsValidating: () => {},
  manualFormationPriority: false,
  setManualFormationPriority: () => {},
  formationRanks: [],
  setFormationRanks: () => {},
  formationsTrigger: 0,
  setFormationsTrigger: () => {},
  formationMappingsReadyFor: null,
  setFormationMappingsReadyFor: () => {},
  savedInventoryConfigHref: null,
  setSavedInventoryConfigHref: () => {},
  focusedSubAreaId: null,
  setFocusedSubAreaId: () => {},
});

type ScenarioFormSubmitMeta = {
  typeCurveScenarioId: string | null;
  areaConfigId: string | null;
  onSuccess?: () => void;
  onError?: () => void;
};

// Exported for regression testing (INS-11853).
export const useCreateScenarioForm = (
  defaultValues: TypeCurveScenarioFormData
) => {
  // `useForm` re-applies its `defaultValues` on EVERY render (its `update()`
  // runs in a dependency-less layout effect). Passing the live-mutating
  // `defaultValues` state means each `setDefaultValues(...)` re-fires `update()`,
  // which — while the form reads untouched — resets `form.state.values`, which
  // notifies `@tanstack/store`, which synchronously re-renders every
  // `form.Subscribe`/`useStore` consumer via `useSyncExternalStore`, one of
  // which writes a field again → infinite loop → React #185 (INS-11853). This
  // only diverges under React 18's tighter scheduling (it converged under 17).
  // Freeze the defaults handed to `useForm` so `update()` has nothing to
  // re-apply; the live scenario data is pushed through `form.setFieldValue` in
  // the hydration/onChange handlers, and dirty-tracking uses the separate
  // `baselines` state via `isFormModified`, so nothing here depends on the
  // reset behavior we're removing.
  const initialDefaults = useRef(defaultValues).current;
  return useForm({
    defaultValues: initialDefaults,
    validationLogic: revalidateLogic({
      mode: 'change',
      modeAfterSubmission: 'change',
    }),
  });
};

interface ScenarioFormProviderProps {
  children: React.ReactNode;
  readOnly?: boolean;
}

// Form-side defaults shown to the user before any save. Baselines are
// initialized to the same object so isFormModified is false on initial
// load. resetConfigId is responsible for diverging values from baselines
// when the user clicks Add to start a new area config, which is what
// makes the section-level dirty checks fire on first save.
export const INITIAL_FORM_VALUES: TypeCurveScenarioFormData = {
  globalConfig: scenarioFormDefaultValues.globalConfig,
  areaConfig: null,
  formationMappings: [],
  inventoryConfig: null,
  typeWellConfigs: [],
  scalingConfigs: [],
  validationConfigs: scenarioFormDefaultValues.validationConfigs,
};

export const ScenarioFormProvider = ({
  children,
  readOnly = false,
}: ScenarioFormProviderProps) => {
  const [defaultValues, setDefaultValues] =
    useState<TypeCurveScenarioFormData>(INITIAL_FORM_VALUES);

  const [baselines, setBaselines] =
    useState<TypeCurveScenarioFormData>(INITIAL_FORM_VALUES);

  const [discardCount, setDiscardCount] = useState(0);
  const [saveCount, setSaveCount] = useState(0);

  const history = useHistory();

  const updateScenario = useSetAtom(updateScenarioAtom);
  const saveAreaConfig = useSetAtom(saveAreaConfigAtom);
  const updateAreaConfig = useSetAtom(updateAreaConfigAtom);
  const saveTypeCurveScenario = useSetAtom(saveTypeCurveScenarioAtom);
  const saveFormationMappings = useSetAtom(saveFormationMappingsAtom);
  const saveInventoryConfig = useSetAtom(saveInventoryConfigAtom);
  const updateInventoryConfig = useSetAtom(updateInventoryConfigAtom);
  const saveScalingConfigs = useSetAtom(saveScalingConfigsAtom);
  const saveTypeWellConfigs = useSetAtom(saveTypeWellConfigsAtom);
  const saveValidationConfigs = useSetAtom(saveValidationConfigsAtom);
  const setScenarioData = useSetAtom(typeCurveScenarioDataAtom);

  const [areaConfigs, setAreaConfigs] = useState<AreaConfigResponse[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(
    SCENARIO_DETAILS_ID
  );
  const [subAreaGeometries, setSubAreaGeometries] = useState<
    Record<string, Geometry>
  >({});
  const [structureGridCollectionData, setStructureGridCollectionData] =
    useState<StructureGridCollectionDetail | null>(null);
  const [validConfigState, setValidConfigState] = useState<{
    isValid: boolean;
    message: string;
  } | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [manualFormationPriority, setManualFormationPriority] = useState(false);
  const [formationRanks, setFormationRanks] = useState<number[]>([]);
  const [formationsTrigger, setFormationsTrigger] = useState(0);
  const [formationMappingsReadyFor, setFormationMappingsReadyFor] = useState<
    string | null
  >(null);
  const [savedInventoryConfigHref, setSavedInventoryConfigHref] = useState<
    string | null
  >(null);
  const [focusedSubAreaId, setFocusedSubAreaId] = useState<string | null>(null);

  const form = useCreateScenarioForm(defaultValues);

  const isScenarioDetailsSelected = selectedConfigId === SCENARIO_DETAILS_ID;

  const selectedAreaConfig: AreaConfigResponse | null = useMemo(() => {
    if (isScenarioDetailsSelected) return null;
    return areaConfigs.find((config) => config.id === selectedConfigId) || null;
  }, [areaConfigs, selectedConfigId, isScenarioDetailsSelected]);

  const setFieldBaseline = useCallback<SetFieldBaselineFn>(
    (field, value) => {
      form.setFieldValue(field, value);
      setDefaultValues((prev) => ({ ...prev, [field]: value }));
      setBaselines((prev) => ({ ...prev, [field]: value }));
    },
    [form]
  );

  const updateFieldDefault = useCallback<UpdateFieldDefaultFn>(
    (field, value) => {
      setDefaultValues((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const resetForm = useCallback(() => {
    const currentValues = { ...form.state.values };
    setDefaultValues(currentValues);
    setBaselines(currentValues);
  }, [form]);

  const discardFormChanges = useCallback(() => {
    form.setFieldValue('globalConfig', baselines.globalConfig);
    setFieldBaseline('areaConfig', baselines.areaConfig);
    setFieldBaseline('formationMappings', baselines.formationMappings);
    setFieldBaseline('inventoryConfig', baselines.inventoryConfig);
    setFieldBaseline('typeWellConfigs', baselines.typeWellConfigs);
    setFieldBaseline('scalingConfigs', baselines.scalingConfigs);
    setFieldBaseline('validationConfigs', baselines.validationConfigs);
    setDiscardCount((prev) => prev + 1);
  }, [form, baselines, setFieldBaseline, setDiscardCount]);

  const isAreaConfigMeaningfullyChanged = useCallback(
    (current: AreaConfig | null, baseline: AreaConfig | null) => {
      if (!current && !baseline) return false;
      if (!current || !baseline) return true;
      // subAreaCategory alone is not a meaningful change
      return !isEqual(
        omit(current, 'subAreaCategory'),
        omit(baseline, 'subAreaCategory')
      );
    },
    []
  );

  const isFormModified = useCallback(
    (values: TypeCurveScenarioFormData) => {
      return (
        (!!values.globalConfig &&
          !isEqual(values.globalConfig, baselines.globalConfig)) ||
        isAreaConfigMeaningfullyChanged(
          values.areaConfig,
          baselines.areaConfig
        ) ||
        !isEqual(values.formationMappings, baselines.formationMappings) ||
        !isEqual(values.inventoryConfig, baselines.inventoryConfig) ||
        !isEqual(values.typeWellConfigs, baselines.typeWellConfigs) ||
        !isEqual(values.scalingConfigs, baselines.scalingConfigs) ||
        !isEqual(values.validationConfigs, baselines.validationConfigs)
      );
    },
    [baselines, isAreaConfigMeaningfullyChanged]
  );

  const handleSubmitForm = useCallback(
    async (meta: ScenarioFormSubmitMeta) => {
      const { values } = form.state;

      try {
        if (meta.typeCurveScenarioId) {
          // Update existing scenario
          const globalConfigChanged = !isEqual(
            values.globalConfig,
            baselines.globalConfig
          );

          if (globalConfigChanged) {
            await updateScenario({
              payload: {
                typeCurveScenarioId: meta.typeCurveScenarioId,
                globalConfig: getChangedObject(
                  values.globalConfig,
                  baselines.globalConfig
                ) as Partial<GlobalConfig>,
              },
              onSuccess: () => {
                // globalConfig is not loaded from href, reset baseline here
                setBaselines((prev) => ({
                  ...prev,
                  globalConfig: values.globalConfig,
                }));
                setDefaultValues((prev) => ({
                  ...prev,
                  globalConfig: values.globalConfig,
                }));
              },
            });
          }

          let resolvedAreaConfigId: string | null = meta.areaConfigId;
          let savedAreaConfigResponse: AreaConfigResponse | null = null;

          if (meta.areaConfigId) {
            // Update existing area config
            const areaConfigChanged = !isEqual(
              values.areaConfig,
              baselines.areaConfig
            );

            if (areaConfigChanged && values.areaConfig) {
              await updateAreaConfig({
                payload: {
                  typeCurveScenarioId: meta.typeCurveScenarioId,
                  areaConfigId: meta.areaConfigId,
                  areaConfig: getChangedObject(
                    values.areaConfig,
                    baselines.areaConfig
                  ) as Partial<AreaConfig>,
                  fullAreaConfig: values.areaConfig,
                },
                onSuccess: ({ areaConfig }) => {
                  setAreaConfigs((prev) =>
                    prev.map((config) => {
                      if (config.id === areaConfig.id) {
                        return { ...areaConfig, elementId: config.elementId };
                      }
                      return config;
                    })
                  );
                },
              });
            }

            // Update area config baseline from current form values
            setBaselines((prev) => ({
              ...prev,
              areaConfig: values.areaConfig,
            }));
            setDefaultValues((prev) => ({
              ...prev,
              areaConfig: values.areaConfig,
            }));
          } else if (values.areaConfig && !isScenarioDetailsSelected) {
            // Insert new area config for existing scenario
            await saveAreaConfig({
              payload: {
                typeCurveScenarioId: meta.typeCurveScenarioId,
                areaConfig: values.areaConfig,
              },
              onSuccess: ({ areaConfig }) => {
                resolvedAreaConfigId = areaConfig.id;
                // Defer setAreaConfigs/setSelectedConfigId to after all
                // section saves, so resource URLs don't appear until data
                // is persisted (DCA pattern).
                savedAreaConfigResponse = areaConfig;
              },
            });
            // Area config baseline update is deferred to batch with config
            // promotion below — avoids intermediate render where baselines
            // change but config is still 'local' (which triggers validation).
          }

          // For each section we use a simple dirty check at the section level;
          // the atoms themselves decide POST vs PATCH per row based on whether
          // the row has a server-assigned id.
          //
          // For new area configs (no meta.areaConfigId), `resetConfigId` keeps
          // values = baselines (defaults) so isFormModified stays false until
          // the user actually changes something. To still POST those default
          // sub-configs on first save, we force-save every section when the
          // area config was just created in this submit.
          const isNewArea = !meta.areaConfigId && !!resolvedAreaConfigId;

          // Save formation mappings
          if (
            resolvedAreaConfigId &&
            (isNewArea ||
              !isEqual(values.formationMappings, baselines.formationMappings))
          ) {
            await saveFormationMappings({
              payload: {
                typeCurveScenarioId: meta.typeCurveScenarioId,
                areaConfigId: resolvedAreaConfigId,
                formationMappings: values.formationMappings,
                baselineFormationMappings: baselines.formationMappings,
                algorithmicFormationPriority:
                  values.areaConfig?.algorithmicFormationPriority ?? true,
              },
              onSuccess: (updatedMappings) => {
                // Update form state with server-assigned IDs and reset baselines
                form.setFieldValue('formationMappings', updatedMappings);
                setBaselines((prev) => ({
                  ...prev,
                  formationMappings: updatedMappings,
                }));
                setDefaultValues((prev) => ({
                  ...prev,
                  formationMappings: updatedMappings,
                }));
              },
            });
          }

          // Save inventory config: PATCH if we already have a server-saved
          // href, POST otherwise.
          if (
            resolvedAreaConfigId &&
            values.inventoryConfig &&
            (isNewArea ||
              !isEqual(values.inventoryConfig, baselines.inventoryConfig))
          ) {
            if (savedInventoryConfigHref) {
              await updateInventoryConfig({
                payload: {
                  inventoryConfigHref: savedInventoryConfigHref,
                  inventoryConfig: values.inventoryConfig,
                },
                onSuccess: (data) => {
                  setSavedInventoryConfigHref(data.href);
                  setBaselines((prev) => ({
                    ...prev,
                    inventoryConfig: values.inventoryConfig,
                  }));
                  setDefaultValues((prev) => ({
                    ...prev,
                    inventoryConfig: values.inventoryConfig,
                  }));
                },
              });
            } else {
              await saveInventoryConfig({
                payload: {
                  typeCurveScenarioId: meta.typeCurveScenarioId,
                  areaConfigId: resolvedAreaConfigId,
                  inventoryConfig: values.inventoryConfig,
                },
                onSuccess: (data) => {
                  setSavedInventoryConfigHref(data.href);
                  setBaselines((prev) => ({
                    ...prev,
                    inventoryConfig: values.inventoryConfig,
                  }));
                  setDefaultValues((prev) => ({
                    ...prev,
                    inventoryConfig: values.inventoryConfig,
                  }));
                },
              });
            }
          }

          // Save scaling configs
          if (
            resolvedAreaConfigId &&
            (isNewArea ||
              !isEqual(values.scalingConfigs, baselines.scalingConfigs))
          ) {
            await saveScalingConfigs({
              payload: {
                typeCurveScenarioId: meta.typeCurveScenarioId,
                areaConfigId: resolvedAreaConfigId,
                scalingConfigs: values.scalingConfigs,
                baselineScalingConfigs: baselines.scalingConfigs,
              },
              onSuccess: (updatedConfigs) => {
                form.setFieldValue('scalingConfigs', updatedConfigs);
                setBaselines((prev) => ({
                  ...prev,
                  scalingConfigs: updatedConfigs,
                }));
                setDefaultValues((prev) => ({
                  ...prev,
                  scalingConfigs: updatedConfigs,
                }));
              },
            });
          }

          // Save type well configs
          if (
            resolvedAreaConfigId &&
            (isNewArea ||
              !isEqual(values.typeWellConfigs, baselines.typeWellConfigs))
          ) {
            await saveTypeWellConfigs({
              payload: {
                typeCurveScenarioId: meta.typeCurveScenarioId,
                areaConfigId: resolvedAreaConfigId,
                typeWellConfigs: values.typeWellConfigs,
                baselineTypeWellConfigs: baselines.typeWellConfigs,
              },
              onSaved: (rows, savedRows) => {
                form.setFieldValue('typeWellConfigs', rows);
                setBaselines((prev) => ({
                  ...prev,
                  typeWellConfigs: savedRows,
                }));
                setDefaultValues((prev) => ({
                  ...prev,
                  typeWellConfigs: savedRows,
                }));
              },
            });
          }

          // Save validation configs
          if (
            resolvedAreaConfigId &&
            (isNewArea ||
              !isEqual(values.validationConfigs, baselines.validationConfigs))
          ) {
            await saveValidationConfigs({
              payload: {
                typeCurveScenarioId: meta.typeCurveScenarioId,
                areaConfigId: resolvedAreaConfigId,
                validationConfigs: values.validationConfigs,
                baselineValidationConfigs: baselines.validationConfigs,
              },
              onSuccess: (updatedConfigs) => {
                form.setFieldValue('validationConfigs', updatedConfigs);
                setBaselines((prev) => ({
                  ...prev,
                  validationConfigs: updatedConfigs,
                }));
                setDefaultValues((prev) => ({
                  ...prev,
                  validationConfigs: updatedConfigs,
                }));
              },
            });
          }

          // Promote local → server config AFTER all section saves complete,
          // so resource URLs don't trigger fetches until data is persisted.
          // Baseline update is batched here so validation sees isSavedConfig=true
          // in the same render as the matching baseline keys.
          // Type assertion needed: CFA cannot track callback assignments.
          const deferredAreaConfig =
            savedAreaConfigResponse as AreaConfigResponse | null;
          if (deferredAreaConfig) {
            // React 17 doesn't auto-batch setState calls in async callbacks
            // (only inside React event handlers). Without this wrapper, each
            // setter triggers its own render — including an intermediate
            // render where areaConfigs has the new server id but
            // selectedConfigId is still 'local', which makes
            // selectedAreaConfig.find(...) return null and unmounts
            // <AreaConfigs />. Batching collapses all four updates into a
            // single render and avoids the unmount/remount.
            unstable_batchedUpdates(() => {
              setBaselines((prev) => ({
                ...prev,
                areaConfig: values.areaConfig,
              }));
              setDefaultValues((prev) => ({
                ...prev,
                areaConfig: values.areaConfig,
              }));
              // Preserve the local config's elementId so the area-configs
              // reset effect (keyed on elementId) sees no change here — the
              // server id flip from 'local' → real id is intentionally
              // invisible to that effect.
              setAreaConfigs((prev) =>
                prev.map((config) => {
                  if (config.id === 'local') {
                    return {
                      ...deferredAreaConfig,
                      elementId: config.elementId,
                    };
                  }
                  return config;
                })
              );
              setSelectedConfigId(deferredAreaConfig.id);
            });
          }
        } else {
          // Create new scenario
          let newScenarioId: string | null = null;
          let newAreaConfigId: string | null = null;

          await saveTypeCurveScenario({
            payload: {
              globalConfig: values.globalConfig,
              areaConfig: values.areaConfig,
              formData: values,
            },
            onSuccess: (data) => {
              newScenarioId = data.typeCurveScenarioId;
              newAreaConfigId = data.areaConfig?.id ?? null;
            },
          });

          // New scenario branch: any area config saved here is brand new, so
          // force-POST all sub-config sections regardless of dirty check
          // (resetConfigId keeps values = baselines = defaults so isModified
          // stays false until the user changes something, but on save we still
          // need to persist the defaults).
          const isNewArea = !!newAreaConfigId;

          // Save formation mappings for new scenario
          if (
            newScenarioId &&
            newAreaConfigId &&
            (isNewArea ||
              !isEqual(values.formationMappings, baselines.formationMappings))
          ) {
            await saveFormationMappings({
              payload: {
                typeCurveScenarioId: newScenarioId,
                areaConfigId: newAreaConfigId,
                formationMappings: values.formationMappings,
                baselineFormationMappings: baselines.formationMappings,
                algorithmicFormationPriority:
                  values.areaConfig?.algorithmicFormationPriority ?? true,
              },
              onSuccess: (updatedMappings) => {
                form.setFieldValue('formationMappings', updatedMappings);
                setBaselines((prev) => ({
                  ...prev,
                  formationMappings: updatedMappings,
                }));
                setDefaultValues((prev) => ({
                  ...prev,
                  formationMappings: updatedMappings,
                }));
              },
            });
          }

          // Save inventory config for new scenario
          if (
            newScenarioId &&
            newAreaConfigId &&
            values.inventoryConfig &&
            (isNewArea ||
              !isEqual(values.inventoryConfig, baselines.inventoryConfig))
          ) {
            await saveInventoryConfig({
              payload: {
                typeCurveScenarioId: newScenarioId,
                areaConfigId: newAreaConfigId,
                inventoryConfig: values.inventoryConfig,
              },
              onSuccess: (data) => {
                setSavedInventoryConfigHref(data.href);
                setBaselines((prev) => ({
                  ...prev,
                  inventoryConfig: values.inventoryConfig,
                }));
                setDefaultValues((prev) => ({
                  ...prev,
                  inventoryConfig: values.inventoryConfig,
                }));
              },
            });
          }

          // Save scaling configs for new scenario
          if (
            newScenarioId &&
            newAreaConfigId &&
            (isNewArea ||
              !isEqual(values.scalingConfigs, baselines.scalingConfigs))
          ) {
            await saveScalingConfigs({
              payload: {
                typeCurveScenarioId: newScenarioId,
                areaConfigId: newAreaConfigId,
                scalingConfigs: values.scalingConfigs,
                baselineScalingConfigs: baselines.scalingConfigs,
              },
              onSuccess: (updatedConfigs) => {
                form.setFieldValue('scalingConfigs', updatedConfigs);
                setBaselines((prev) => ({
                  ...prev,
                  scalingConfigs: updatedConfigs,
                }));
                setDefaultValues((prev) => ({
                  ...prev,
                  scalingConfigs: updatedConfigs,
                }));
              },
            });
          }

          // Save type well configs for new scenario
          if (
            newScenarioId &&
            newAreaConfigId &&
            (isNewArea ||
              !isEqual(values.typeWellConfigs, baselines.typeWellConfigs))
          ) {
            await saveTypeWellConfigs({
              payload: {
                typeCurveScenarioId: newScenarioId,
                areaConfigId: newAreaConfigId,
                typeWellConfigs: values.typeWellConfigs,
                baselineTypeWellConfigs: baselines.typeWellConfigs,
              },
              onSaved: (rows, savedRows) => {
                form.setFieldValue('typeWellConfigs', rows);
                setBaselines((prev) => ({
                  ...prev,
                  typeWellConfigs: savedRows,
                }));
                setDefaultValues((prev) => ({
                  ...prev,
                  typeWellConfigs: savedRows,
                }));
              },
            });
          }

          // Save validation configs for new scenario
          if (
            newScenarioId &&
            newAreaConfigId &&
            (isNewArea ||
              !isEqual(values.validationConfigs, baselines.validationConfigs))
          ) {
            await saveValidationConfigs({
              payload: {
                typeCurveScenarioId: newScenarioId,
                areaConfigId: newAreaConfigId,
                validationConfigs: values.validationConfigs,
                baselineValidationConfigs: baselines.validationConfigs,
              },
              onSuccess: (updatedConfigs) => {
                form.setFieldValue('validationConfigs', updatedConfigs);
                setBaselines((prev) => ({
                  ...prev,
                  validationConfigs: updatedConfigs,
                }));
                setDefaultValues((prev) => ({
                  ...prev,
                  validationConfigs: updatedConfigs,
                }));
              },
            });
          }

          if (newScenarioId) {
            history.replace(`/type_curve_scenarios/${newScenarioId}`);
          }
        }

        setSaveCount((prev) => prev + 1);
        meta.onSuccess?.();
      } catch {
        // Atom already set the error state on typeCurveScenarioDataAtom.
        // Do NOT increment saveCount — form stays as-is so the user can retry.
        meta.onError?.();
      }
    },
    [
      form,
      baselines,
      isScenarioDetailsSelected,
      history,
      updateScenario,
      saveAreaConfig,
      updateAreaConfig,
      saveTypeCurveScenario,
      saveFormationMappings,
      saveInventoryConfig,
      updateInventoryConfig,
      savedInventoryConfigHref,
      saveScalingConfigs,
      saveTypeWellConfigs,
      saveValidationConfigs,
    ]
  );

  const resetConfigId = useCallback(
    (id: string) => {
      if (selectedConfigId === id) return;
      setSelectedConfigId(id);
      // A failed save's error is about the config being left.
      setScenarioData((prev) => ({ ...prev, error: null }));
      setValidConfigState(null);
      setIsValidating(false);
      setStructureGridCollectionData(null);
      setSavedInventoryConfigHref(null);

      // Clear formation-related state so ranks from previous config don't persist
      setFormationRanks([]);
      setManualFormationPriority(false);
      setFormationMappingsReadyFor(null);

      // For new configs (no href), seed form values with defaults AND set
      // baselines to the same defaults — isFormModified must be false right
      // after clicking Add (matches DCA UX). handleSubmitForm detects the
      // new-area-config case (meta.areaConfigId == null) and force-POSTs the
      // default sub-configs even though the dirty check returns false.
      // For saved configs (has href), clear both — data-load effects will
      // populate values and baselines from the API once the resource fetch
      // completes.
      const targetConfig = areaConfigs.find((c) => c.id === id);
      const hasSavedData = !!targetConfig?.href;

      setFieldBaseline('areaConfig', null);
      setFieldBaseline('formationMappings', []);

      if (hasSavedData) {
        setFieldBaseline('inventoryConfig', null);
        setFieldBaseline('typeWellConfigs', []);
        setFieldBaseline('scalingConfigs', []);
        setFieldBaseline('validationConfigs', {
          generalRules: [],
          streamThresholds: [],
        });
      } else {
        // New area config: form value = baseline = defaults so isFormModified
        // stays false until the user makes an actual change.
        setFieldBaseline('inventoryConfig', { ...defaultInventoryConfig });
        // A row for each formation is added once the Formations table has
        // formations.
        setFieldBaseline('typeWellConfigs', [
          { ...defaultFallbackTypeWellConfig },
        ]);
        setFieldBaseline('scalingConfigs', []);
        setFieldBaseline('validationConfigs', {
          generalRules: [],
          streamThresholds: [],
        });
      }
    },
    [selectedConfigId, setFieldBaseline, areaConfigs, setScenarioData]
  );

  return (
    <ScenarioFormContext.Provider
      value={{
        form,
        defaultValues,
        baselines,
        setFieldBaseline,
        updateFieldDefault,
        resetForm,
        discardFormChanges,
        isFormModified,
        areaConfigs,
        setAreaConfigs,
        selectedConfigId,
        selectedAreaConfig,
        isScenarioDetailsSelected,
        resetConfigId,
        setSelectedConfigId,
        handleSubmitForm,
        discardCount,
        setDiscardCount,
        saveCount,
        readOnly,
        subAreaGeometries,
        setSubAreaGeometries,
        structureGridCollectionData,
        setStructureGridCollectionData,
        validConfigState,
        setValidConfigState,
        isValidating,
        setIsValidating,
        manualFormationPriority,
        setManualFormationPriority,
        formationRanks,
        setFormationRanks,
        formationsTrigger,
        setFormationsTrigger,
        formationMappingsReadyFor,
        setFormationMappingsReadyFor,
        savedInventoryConfigHref,
        setSavedInventoryConfigHref,
        focusedSubAreaId,
        setFocusedSubAreaId,
      }}
    >
      {children}
    </ScenarioFormContext.Provider>
  );
};

export const useScenarioForm = () => {
  const context = useContext(ScenarioFormContext);
  if (
    !context?.form ||
    context.defaultValues === null ||
    context.baselines === null ||
    !context.setFieldBaseline ||
    !context.updateFieldDefault ||
    !context.resetForm ||
    !context.discardFormChanges ||
    !context.isFormModified
  ) {
    throw new Error(
      'useScenarioForm must be used within a ScenarioFormProvider'
    );
  }
  return context as Omit<
    ScenarioFormContextType,
    | 'form'
    | 'defaultValues'
    | 'baselines'
    | 'setFieldBaseline'
    | 'updateFieldDefault'
    | 'resetForm'
    | 'discardFormChanges'
    | 'isFormModified'
    | 'discardCount'
    | 'setDiscardCount'
  > & {
    form: ScenarioFormInstance;
    defaultValues: TypeCurveScenarioFormData;
    baselines: TypeCurveScenarioFormData;
    setFieldBaseline: SetFieldBaselineFn;
    updateFieldDefault: UpdateFieldDefaultFn;
    resetForm: () => void;
    discardFormChanges: () => void;
    isFormModified: (values: TypeCurveScenarioFormData) => boolean;
    discardCount: number;
    setDiscardCount: React.Dispatch<React.SetStateAction<number>>;
  };
};
